import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import {
  FiEdit3,
  FiTrash2,
  FiCalendar,
  FiDollarSign,
  FiSettings,
  FiPlus,
  FiEye,
  FiUser,
  FiTool,
  FiAlertCircle,
  FiArrowLeft,
  FiShield
} from 'react-icons/fi';

const CarDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [car, setCar] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [maintenanceForm, setMaintenanceForm] = useState({
    type: '',
    description: '',
    cost: '',
    date: new Date().toISOString().split('T')[0],
    mileage: '',
    serviceProvider: ''
  });

  useEffect(() => {
    fetchCarDetails();
  }, [id]);

  const fetchCarDetails = async () => {
    try {
      setLoading(true);
      const [carRes, reservationsRes] = await Promise.all([
        api.get(`/api/cars/${id}`),
        api.get(`/api/reservations?car=${id}`)
      ]);

      setCar(carRes.data.data);
      setReservations(reservationsRes.data.data);
      setEditForm(carRes.data.data);

      // Mock maintenance records (in real app, this would come from API)
      // setMaintenanceRecords([
      //   {
      //     id: 1,
      //     type: 'Oil Change',
      //     description: 'Regular oil change and filter replacement',
      //     cost: 45.00,
      //     date: '2024-08-15',
      //     mileage: 45000,
      //     serviceProvider: 'Auto Service Center'
      //   },
      //   {
      //     id: 2,
      //     type: 'Tire Rotation',
      //     description: 'Tire rotation and balance',
      //     cost: 35.00,
      //     date: '2024-07-20',
      //     mileage: 43000,
      //     serviceProvider: 'Quick Tire Shop'
      //   }
      // ]);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails de la voiture');
      console.error('Error fetching car details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    try {
      await api.put(`/api/cars/${id}`, editForm);
      setCar({ ...car, ...editForm });
      setShowEditModal(false);
      toast.success('Voiture mise à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
      console.error('Error updating car:', error);
    }
  };

  const handleAddMaintenance = async () => {
    try {
      const newMaintenance = {
        ...maintenanceForm,
        id: Date.now(),
        cost: parseFloat(maintenanceForm.cost),
        mileage: parseInt(maintenanceForm.mileage)
      };

      setMaintenanceRecords([newMaintenance, ...maintenanceRecords]);
      setMaintenanceForm({
        type: '',
        description: '',
        cost: '',
        date: new Date().toISOString().split('T')[0],
        mileage: '',
        serviceProvider: ''
      });
      setShowMaintenanceModal(false);
      toast.success('Maintenance ajoutée avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de la maintenance');
      console.error('Error adding maintenance:', error);
    }
  };

  const handleDeleteCar = async () => {
    try {
      await api.delete(`/api/cars/${id}`);
      toast.success('Voiture supprimée avec succès');
      navigate('/cars');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      console.error('Error deleting car:', error);
    }
  };

  const updateCarStatus = async (newStatus) => {
    try {
      await api.put(`/api/cars/${id}`, { status: newStatus });
      setCar({ ...car, status: newStatus });
      toast.success(`Statut mis à jour: ${newStatus}`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
      console.error('Error updating status:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return 'bg-green-100 text-green-800';
      case 'Rented': return 'bg-blue-100 text-blue-800';
      case 'Maintenance': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-red-100 text-red-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'Available': return 'DISPONIBLE';
      case 'Rented': return 'LOUÉ';
      case 'Maintenance': return 'MAINTENANCE';
      default: return 'HORS SERVICE';
    }
  };

  const calculateRevenue = () => {
    return reservations
      .filter(r => r.status === 'Completed')
      .reduce((total, r) => total + (r.totalPrice || 0), 0);
  };

  const calculateUtilization = () => {
    const totalDays = 365;
    const rentedDays = reservations
      .filter(r => r.status === 'Completed' || r.status === 'Active')
      .reduce((total, r) => {
        const start = new Date(r.startDate);
        const end = new Date(r.endDate);
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        return total + days;
      }, 0);
    return Math.round((rentedDays / totalDays) * 100);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Voiture non trouvée</h2>
          <p className="text-gray-600 mt-2">La voiture demandée n'existe pas.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 bg-gray-100 min-h-screen">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/cars')}
            className="p-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 flex-shrink-0"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">Détails de la Voiture</h1>
            <p className="text-sm sm:text-base text-gray-600">{car.brand} {car.model} - {car.licensePlate}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowEditModal(true)}
            className="px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 text-sm"
          >
            <FiEdit3 className="w-4 h-4" />
            <span className="hidden sm:inline">Modifier</span>
            <span className="sm:hidden">Modif.</span>
          </button>
          <button
            onClick={() => setShowMaintenanceModal(true)}
            className="px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 text-sm"
          >
            <FiPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Maintenance</span>
            <span className="sm:hidden">Maint.</span>
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="px-3 sm:px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2 text-sm"
          >
            <FiTrash2 className="w-4 h-4" />
            <span className="hidden sm:inline">Supprimer</span>
            <span className="sm:hidden">Suppr.</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiDollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Revenus Totaux</p>
              <p className="text-xl font-bold text-gray-900">{calculateRevenue().toFixed(2)} MAD</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <FiCalendar className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Réservations</p>
              <p className="text-xl font-bold text-gray-900">{reservations.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <FiTool className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Maintenance</p>
              <p className="text-xl font-bold text-gray-900">{maintenanceRecords.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FiSettings className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Utilisation</p>
              <p className="text-xl font-bold text-gray-900">{calculateUtilization()}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Status Update */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Statut Actuel</h3>
            <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium mt-1 ${getStatusColor(car.status)}`}>
              {getStatusText(car.status)}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => updateCarStatus('Available')}
              className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium ${car.status === 'Available'
                ? 'bg-green-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              Disponible
            </button>
            <button
              onClick={() => updateCarStatus('Maintenance')}
              className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium ${car.status === 'Maintenance'
                ? 'bg-yellow-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              Maintenance
            </button>
            <button
              onClick={() => updateCarStatus('Out of Service')}
              className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium ${car.status === 'Out of Service'
                ? 'bg-red-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              <span className="hidden sm:inline">Hors Service</span>
              <span className="sm:hidden">H.S.</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex space-x-2 sm:space-x-6 px-3 sm:px-6 min-w-max">
            {[
              { id: 'overview', name: 'Vue d\'ensemble', shortName: 'Vue', icon: FiEye },
              { id: 'reservations', name: 'Réservations', shortName: 'Réserv.', icon: FiCalendar },
              { id: 'maintenance', name: 'Maintenance', shortName: 'Maint.', icon: FiTool },
              { id: 'insurance', name: 'Assurance', shortName: 'Assur.', icon: FiShield },
              { id: 'financial', name: 'Financier', shortName: 'Financ.', icon: FiDollarSign }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.name}</span>
                <span className="sm:hidden">{tab.shortName}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations Générales</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Marque:</span>
                    <span className="font-medium">{car.brand}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Modèle:</span>
                    <span className="font-medium">{car.model}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Année:</span>
                    <span className="font-medium">{car.year}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plaque:</span>
                    <span className="font-medium">{car.licensePlate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Couleur:</span>
                    <span className="font-medium">{car.color || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Kilométrage:</span>
                    <span className="font-medium">{car.mileage || 'N/A'} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transmission:</span>
                    <span className="font-medium">
                      {car.transmission === 'Automatic' ? 'Automatique' :
                        car.transmission === 'Manual' ? 'Manuelle' :
                          car.transmission === 'CVT' ? 'CVT' : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tarifs</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Prix journalier:</span>
                    <span className="font-medium">{car.dailyPrice || 'N/A'} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Prix hebdomadaire:</span>
                    <span className="font-medium">{car.weeklyPrice || 'N/A'} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Prix mensuel:</span>
                    <span className="font-medium">{car.monthlyPrice || 'N/A'} €</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reservations Tab */}
          {activeTab === 'reservations' && (
            <div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Historique des Réservations</h3>
                <button
                  onClick={() => navigate(`/calendar?car=${car._id}`)}
                  className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 text-sm w-fit"
                >
                  <FiCalendar className="w-4 h-4" />
                  <span className="hidden sm:inline">Voir Calendrier</span>
                  <span className="sm:hidden">Calendrier</span>
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fin</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reservations.map((reservation) => (
                      <tr key={reservation._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <FiUser className="w-5 h-5 text-gray-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {reservation.client?.name || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {reservation.client?.phone || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(reservation.startDate).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(reservation.endDate).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(reservation.status)}`}>
                            {reservation.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {reservation.totalPrice?.toFixed(2) || 'N/A'} €
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Maintenance Tab */}
          {activeTab === 'maintenance' && (
            <div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Historique de Maintenance</h3>
                <button
                  onClick={() => setShowMaintenanceModal(true)}
                  className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 text-sm w-fit"
                >
                  <FiPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">Ajouter Maintenance</span>
                  <span className="sm:hidden">Ajouter</span>
                </button>
              </div>

              <div className="space-y-4">
                {maintenanceRecords.map((record) => (
                  <div key={record.id} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900">{record.type}</h4>
                        <p className="text-sm text-gray-600 mt-1">{record.description}</p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                          <span>Date: {record.date}</span>
                          <span>Kilométrage: {record.mileage} km</span>
                          <span>Prestataire: {record.serviceProvider}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-gray-900">{record.cost} MAD</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Insurance Tab */}
          {activeTab === 'insurance' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Informations d'Assurance</h3>
                <button
                  onClick={() => navigate('/insurance')}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2"
                >
                  <FiShield className="w-4 h-4" />
                  <span>Gérer les Assurances</span>
                </button>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <div className="text-center">
                  <FiShield className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-blue-800 mb-2">Gestion des Assurances</h4>
                  <p className="text-blue-600 mb-4">
                    Gérez les polices d'assurance de cette voiture depuis la page dédiée aux assurances.
                  </p>
                  <button
                    onClick={() => navigate('/insurance')}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Aller aux Assurances
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Financial Tab */}
          {activeTab === 'financial' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Rapport Financier</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800">Revenus Totaux</h4>
                  <p className="text-2xl font-bold text-blue-900">{calculateRevenue().toFixed(2)} MAD</p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-green-800">Réservations Complétées</h4>
                  <p className="text-2xl font-bold text-green-900">
                    {reservations.filter(r => r.status === 'Completed').length}
                  </p>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-purple-800">Taux d'Utilisation</h4>
                  <p className="text-2xl font-bold text-purple-900">{calculateUtilization()}%</p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Détails des Revenus</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Réservations</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenus</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Moyenne</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Ce mois</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {reservations.filter(r => {
                            const start = new Date(r.startDate);
                            const now = new Date();
                            return start.getMonth() === now.getMonth() && start.getFullYear() === now.getFullYear();
                          }).length}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {reservations.filter(r => {
                            const start = new Date(r.startDate);
                            const now = new Date();
                            return start.getMonth() === now.getMonth() && start.getFullYear() === now.getFullYear();
                          }).reduce((total, r) => total + (r.totalPrice || 0), 0).toFixed(2)} MAD
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {reservations.filter(r => {
                            const start = new Date(r.startDate);
                            const now = new Date();
                            return start.getMonth() === now.getMonth() && start.getFullYear() === now.getFullYear();
                          }).length > 0 ?
                            (reservations.filter(r => {
                              const start = new Date(r.startDate);
                              const now = new Date();
                              return start.getMonth() === now.getMonth() && start.getFullYear() === now.getFullYear();
                            }).reduce((total, r) => total + (r.totalPrice || 0), 0) /
                              reservations.filter(r => {
                                const start = new Date(r.startDate);
                                const now = new Date();
                                return start.getMonth() === now.getMonth() && start.getFullYear() === now.getFullYear();
                              }).length).toFixed(2) : '0.00'} MAD
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
            <h2 className="text-lg sm:text-xl font-bold mb-4">Modifier la Voiture</h2>
            <div className="space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Marque</label>
                <input
                  type="text"
                  value={editForm.brand || ''}
                  onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
                <input
                  type="text"
                  value={editForm.model || ''}
                  onChange={(e) => setEditForm({ ...editForm, model: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Année</label>
                <input
                  type="number"
                  value={editForm.year || ''}
                  onChange={(e) => setEditForm({ ...editForm, year: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plaque</label>
                <input
                  type="text"
                  value={editForm.licensePlate || ''}
                  onChange={(e) => setEditForm({ ...editForm, licensePlate: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prix Journalier</label>
                <input
                  type="number"
                  value={editForm.dailyPrice || ''}
                  onChange={(e) => setEditForm({ ...editForm, dailyPrice: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transmission</label>
                <select
                  value={editForm.transmission || 'Automatic'}
                  onChange={(e) => setEditForm({ ...editForm, transmission: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                >
                  <option value="Automatic">Automatique</option>
                  <option value="Manual">Manuelle</option>
                  <option value="CVT">CVT</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Sauvegarder
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Modal */}
      {showMaintenanceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
            <h2 className="text-lg sm:text-xl font-bold mb-4">Ajouter Maintenance</h2>
            <div className="space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={maintenanceForm.type}
                  onChange={(e) => setMaintenanceForm({ ...maintenanceForm, type: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Sélectionner un type</option>
                  <option value="Oil Change">Vidange d'huile</option>
                  <option value="Tire Rotation">Rotation des pneus</option>
                  <option value="Brake Service">Service des freins</option>
                  <option value="Inspection">Inspection</option>
                  <option value="Other">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={maintenanceForm.description}
                  onChange={(e) => setMaintenanceForm({ ...maintenanceForm, description: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  rows="3"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coût (MAD)</label>
                  <input
                    type="number"
                    value={maintenanceForm.cost}
                    onChange={(e) => setMaintenanceForm({ ...maintenanceForm, cost: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={maintenanceForm.date}
                    onChange={(e) => setMaintenanceForm({ ...maintenanceForm, date: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Kilométrage</label>
                  <input
                    type="number"
                    value={maintenanceForm.mileage}
                    onChange={(e) => setMaintenanceForm({ ...maintenanceForm, mileage: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prestataire</label>
                  <input
                    type="text"
                    value={maintenanceForm.serviceProvider}
                    onChange={(e) => setMaintenanceForm({ ...maintenanceForm, serviceProvider: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleAddMaintenance}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                Ajouter
              </button>
              <button
                onClick={() => setShowMaintenanceModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <div className="text-center">
              <FiAlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Supprimer la Voiture</h2>
              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir supprimer {car.brand} {car.model} ? Cette action est irréversible.
              </p>
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={handleDeleteCar}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  Oui, Supprimer
                </button>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarDetail; 